import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {
  Animated,
  Image,
  Platform,
  StyleSheet,
  TouchableOpacity,
  View,
  Easing,
  Text,
  ScrollView,
  AppState,
  Dimensions,
} from 'react-native';
import {useHeaderHeight} from '@react-navigation/elements';
import {useTranslation} from 'react-i18next';
import Constants from 'expo-constants';
import uuid from 'react-native-uuid';
import * as Haptics from 'expo-haptics';
import * as Localization from 'expo-localization';
import * as Device from 'expo-device';
import * as Location from 'expo-location';
import * as Sentry from 'sentry-expo';

import {UserContext} from '../../contexts/UserContext';
import {SettingsContext} from '../../contexts/SettingsContext';
import {ThemeContext} from '../../contexts/ThemeContext';
import Api from '../../constants/Api';
import useRecorder from '../../hooks/useRecorder';
import useSpeaker from '../../hooks/useSpeaker';
import EventSource from '../../utils/SourceEvent';
import {throttle} from '../../utils/throttle';

import Card from './Chat/Card';
import PlacesMap from './Chat/PlacesMap';

let microphonePermissionsRequested = false;

let shouldScroll = false;
let eventSource = null;

const BUTTON_SIZE = 200;
const BORDER_SIZE = 10;

const CustomProgressBar = ({loading, rotationDegree}) => {
  const theme = useContext(ThemeContext);

  const COLOR = theme.light ? 'rgba(0, 0, 0, 0.3)' : '#fff';

  return (
    <View
      accessibilityRole="progressbar"
      style={{
        position: 'absolute',
        width: BUTTON_SIZE + (BORDER_SIZE * 2) - 2,
        height: BUTTON_SIZE + (BORDER_SIZE * 2) - 2,
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 2,
      }}
    >
      <View
        style={{
          width: '100%',
          height: '100%',
          borderRadius: BUTTON_SIZE / 2,
        }}
      />

      {loading && (
        <Animated.View
          style={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            borderRadius: (BUTTON_SIZE + BORDER_SIZE) / 2,
            borderWidth: BORDER_SIZE,
            // Note: Using almost transparent color since transparent does not work on android
            //  see: https://github.com/facebook/react-native/issues/34722
            borderLeftColor: '#00000001',
            borderRightColor: '#00000001',
            borderBottomColor: '#00000001',
            borderTopColor: COLOR,
            transform: [{
              rotateZ: rotationDegree.interpolate({
                inputRange: [0, 360],
                outputRange: ['0deg', '360deg'],
              }),
            }],
          }}
        />
      )}
    </View>
  );
};

const MicrophoneView = ({discussionId, updateDiscussionId, fetchDiscussions}) => {
  const {t} = useTranslation();
  const {userToken, user, deviceToken, setUser} = useContext(UserContext);
  const {settings: {language}} = useContext(SettingsContext);
  const theme = useContext(ThemeContext);
  const styles = useMemo(() => getStyles(theme), [theme]);
  const headerHeight = useHeaderHeight();

  const recorder = useRecorder();
  const speaker = useSpeaker();

  const scrollViewRef = useRef(null);

  const topTextInitialValue = useMemo(() => ({
    text: t('MainScreen.MicrophoneView.Recorder.instruction', 'Push to talk'),
    style: 'system',
  }), [t]);

  const [loading, setLoading] = useState(false);
  const [topText, setTopText] = useState(topTextInitialValue);
  const [lastResponse, setLastResponse] = useState(null);

  useEffect(() => {
    if (discussionId == null) {
      setTopText(topTextInitialValue);
      setLastResponse(null);
    }
  }, [discussionId, topTextInitialValue]);

  // TODO: refactor createQuery with the one in ChatView
  const createQuery = useCallback(async (data, localQueries, localQuery) => {
    const fakeResponseId = uuid.v4();

    try {
      // setQuerying(true);

      if (shouldScroll) {
        setTimeout(() => scrollViewRef && scrollViewRef.current && scrollViewRef.current.scrollToEnd({animated: true}), 0);
      }

      const requestObject = {
        user_token: userToken,
        device_token: deviceToken,
        room: Api.roomSlug,
        stream: true,
        query: {
          discussion_id: discussionId || undefined,
          channel: data.channel,
          ref: data.ref,
          text: data.text,
          recording_id: data.recordingId || undefined,

          country_code: Localization.getLocales()[0].regionCode,
          timezone: Localization.timezone,

          language: language, // fr-FR
          device_brand: Device.brand, // Android: "google", "xiaomi"; iOS: "Apple"; web: null
          device_name: Device.deviceName, // "Vivian's iPhone XS"
          device_manufacturer: Device.manufacturer, // Android: "Google", "xiaomi"; iOS: "Apple"; web: "Google", null
          device_os_name: Platform.OS,
          device_os_version: Device.osVersion,
          device_model_name: Device.modelName,
          expo_session_id: Constants.sessionId,
          expo_installation_id: Constants.installationId,

          screen: 'microphone',
        },
      };

      const deviceTypeIndex = await Device.getDeviceTypeAsync();

      if(deviceTypeIndex === 0)       {
        requestObject.query.device_type = 'unknown';
      } else if(deviceTypeIndex === 1)  {
        requestObject.query.device_type = 'phone';
      } else if(deviceTypeIndex === 2)  {
        requestObject.query.device_type = 'tablet';
      } else if(deviceTypeIndex === 3)  {
        requestObject.query.device_type = 'desktop';
      } else if(deviceTypeIndex === 4)  {
        requestObject.query.device_type = 'tv';
      } else {
        requestObject.query.device_type = 'UNKNOWN';
      }

      let locationPerm = {};

      if (Platform.OS === 'web') {
        const {state} = await navigator.permissions.query({name: 'geolocation'});

        locationPerm = {granted: state === 'granted'};
      } else {
        locationPerm = await Location.getForegroundPermissionsAsync();
      }

      if (locationPerm.granted) {
        let location = await Location.getLastKnownPositionAsync({
          requiredAccuracy: 300, // Note: Return null if ~300 meter away from last known location
          maxAge: 600000, // Note: Return null if last known location is more than 10m old
        });

        if (!location) {
          location = await Location.getCurrentPositionAsync({
            accuracy: Location.Accuracy.High,
            mayShowUserSettingsDialog: false,
          });
        }

        requestObject.query.latitude = location.coords.latitude;
        requestObject.query.longitude = location.coords.longitude;
      }

      let appStateListener = null;

      const createRequest = new Promise((resolve, reject) => {
        eventSource = new EventSource(
          `${Api.apiBaseUrl}/queries.json`,
          {
            method: 'POST',
            headers: {
              'Accept': 'application/json',
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(requestObject),
            // debug: true,
          }
        );

        eventSource.addEventListener('open', () => {
          console.log('Event "open" called');
          shouldScroll = true;

          speaker.initSpeakQueryState();
        });

        const throttledUpdateCurrentQuery = throttle(currentQuery => {
          // updateCurrentQuery(localQueries, currentQuery);
          setLastResponse(currentQuery.responses[0]);

          if (shouldScroll) {
            setTimeout(() => scrollViewRef && scrollViewRef.current && scrollViewRef.current.scrollToEnd({animated: true}), 0);
          }
        }, 300, false, false);

        let message = '';

        eventSource.addEventListener('initial', async event => {
          // setCanStopGenerating(true);

          const currentQuery = JSON.parse(event.data);
          console.log('[Event "initial"] currentQuery: ', currentQuery);

          // Note: If we weren't on a discussion (eg: first use, after a clear discussion) we fetchDiscussions and
          //  update the discussionId and fetchDiscussions to update the drawer content
          if (!discussionId) {
            const fetchAndUpdateDiscussion = async () => {
              await fetchDiscussions();

              console.log('updateDiscussionId()', currentQuery, currentQuery.discussion_id);
              updateDiscussionId(currentQuery.discussion_id);
            };

            fetchAndUpdateDiscussion();
          }

          eventSource.addEventListener('message', event => {
            message += event.data;

            throttledUpdateCurrentQuery({
              ...currentQuery,
              responses: [{
                id: fakeResponseId,
                message,
                created_at: Date.now(),
                channel: currentQuery.channel,
                source: undefined,
                cards: [],
              }],
            });

            speaker.speakToken(event.data, currentQuery.language);
          });

          eventSource.addEventListener('loading_message', event => {
            // TODO:
            throttledUpdateCurrentQuery({
              ...currentQuery,
              responses: [{
                id: fakeResponseId,
                message: event.data,
                created_at: Date.now(),
                channel: currentQuery.channel,
                source: undefined,
                cards: [],
              }],
            });
          });

          if (Platform.OS === 'ios') {
            appStateListener = AppState.addEventListener('change', nextAppState => {
              appStateListener.remove();

              if (nextAppState === 'inactive' || nextAppState === 'background') {
                if (eventSource) {
                  eventSource.removeAllEventListeners();
                  eventSource.close();
                  eventSource = null;
                }

                // setQuerying(false);
                // setCanStopGenerating(false);
              }
            });
          }
        });

        eventSource.addEventListener('final', async event => {
          eventSource.removeAllEventListeners();
          eventSource.close();
          eventSource = null;
          // setQuerying(false);
          // setCanStopGenerating(false);

          const query = JSON.parse(event.data);
          console.log('Event "final": ', query);

          if (query.responses && query.responses.length && query.responses[0].message) {
            speaker.emptyResponseBuffer(query.responses[0].message, query.responses[0].language);
          }

          const updatedUser = {...user, daily_queries: query.daily_queries};

          setUser(updatedUser);

          resolve(query);
        });

        eventSource.addEventListener('error', (event) => {
          // TODO: Handle errors
          if (event.type === 'error') {
            console.log('Connection error:', event.message);
          } else if (event.type === 'exception') {
            console.log('Error:', event.message, event.error);
          }

          // TODO:
          setLastResponse({
            message: t('MainScreen.ChatView.Recorder.errors.recordingFailed', 'Sorry, something went wrong, try again later.'),
          });

          // updateCurrentQuery(localQueries, {
          //   ...localQuery,
          //   responses: [{
          //     id: fakeResponseId,
          //     message: t('MainScreen.ChatView.Recorder.errors.recordingFailed', 'Sorry, something went wrong, try again later.'),
          //     status: 'error',
          //     created_at: Date.now(),
          //     channel: localQuery.channel,
          //     source: undefined,
          //     cards: [],
          //   }],
          // });

          eventSource.removeAllEventListeners();
          eventSource.close();
          eventSource = null;
          // setQuerying(false);
          // setCanStopGenerating(false);
          reject({message: event.message});
        });

        eventSource.addEventListener('close', () => {
          console.log('Close SSE connection.');
          eventSource.removeAllEventListeners();
          eventSource.close();
          eventSource = null;
          // setQuerying(false);
          // setCanStopGenerating(false);
        });
      });

      const result = await createRequest;

      if (appStateListener) {
        appStateListener.remove();
      }

      console.log('createRequest() result', result);

      return result;
    } catch (error) {
      // TODO: Handle errors
      if (Platform.OS === 'web') {
        Sentry.Browser.captureException(error);
      } else {
        Sentry.Native.captureException(error);
      }

      if (eventSource) {
        eventSource.removeAllEventListeners();
        eventSource.close();
        eventSource = null;
        // setQuerying(false);
        // setCanStopGenerating(false);
      }

      // TODO:
      setLastResponse({
        message: t('MainScreen.ChatView.Recorder.errors.recordingFailed', 'Sorry, something went wrong, try again later.'),
      });

      // updateCurrentQuery(localQueries, {
      //   ...localQuery,
      //   responses: [{
      //     id: fakeResponseId,
      //     message: t('MainScreen.ChatView.Recorder.errors.recordingFailed', 'Sorry, something went wrong, try again later.'),
      //     status: 'error',
      //     created_at: Date.now(),
      //     channel: localQuery.channel,
      //     source: undefined,
      //     cards: [],
      //   }],
      // });
    }
  }, [userToken, deviceToken, discussionId, language, speaker, user, setUser, fetchDiscussions, updateDiscussionId, t]);


  // TODO: refactor handleUploadRecording with the one in ChatView
  const handleUploadRecording = useCallback(async (uri, ref) => {
    try {
      const formData = new FormData();

      formData.append('user_token', userToken);
      formData.append('recording[ref]', ref);

      if(Platform.OS === 'web') {
        const blob = await (await fetch(uri)).blob();

        formData.append('recording[file]', blob, 'recording_' + Constants.sessionId + '_' + Date.now() + '.wav');
      } else {
        formData.append('recording[file]', {
          uri,
          type: 'audio/x-wav',
          name: 'recording_' + Constants.sessionId + '_' + Date.now() + '.wav',
        });
      }

      formData.append('recording[language]', language); // fr-FR
      formData.append('recording[device_brand]', Device.brand); // Android: "google", "xiaomi"; iOS: "Apple"; web: null
      formData.append('recording[device_name]', Device.deviceName); // "Vivian's iPhone XS"
      formData.append('recording[device_manufacturer]', Device.manufacturer); // Android: "Google", "xiaomi"; iOS: "Apple"; web: "Google", null
      formData.append('recording[device_os_name]', Platform.OS);
      formData.append('recording[device_os_version]', Device.osVersion);
      formData.append('recording[device_model_name]', Device.modelName);
      formData.append('recording[expo_session_id]', Constants.sessionId);
      formData.append('recording[expo_installation_id]', Constants.installationId);

      if(Platform.OS === 'web') {
        formData.append('recording[encoding]', 'WEBM_OPUS');
        formData.append('recording[sample_rate]', '48000');
        formData.append('recording[bit_rate]', '128000');
      } else if(Platform.OS === 'ios') {
        formData.append('recording[encoding]', 'LINEAR16');
        formData.append('recording[sample_rate]', '48000');
        formData.append('recording[bit_rate]', '128000');
      } else {
        formData.append('recording[encoding]', 'AMR_WB');
        formData.append('recording[sample_rate]', '16000');
        formData.append('recording[bit_rate]', '16000');
      }

      const deviceType = await Device.getDeviceTypeAsync();
      if(deviceType === 0)       { formData.append('recording[device_type]', 'unknown'); }
      else if(deviceType === 1)  { formData.append('recording[device_type]', 'phone'); }
      else if(deviceType === 2)  { formData.append('recording[device_type]', 'tablet'); }
      else if(deviceType === 3)  { formData.append('recording[device_type]', 'desktop'); }
      else if(deviceType === 4)  { formData.append('recording[device_type]', 'tv'); }
      else { formData.append('recording[device_type]', 'UNKNOWN'); }

      let locationPerm = {};

      if (Platform.OS === 'web') {
        const {state} = await navigator.permissions.query({name: 'geolocation'});

        locationPerm = {granted: state === 'granted'};
      } else {
        locationPerm = await Location.getForegroundPermissionsAsync();
      }

      if (locationPerm.granted) {
        let location = await Location.getLastKnownPositionAsync({
          requiredAccuracy: 300, // Note: Return null if ~300 meter away from last known location
          maxAge: 600000, // Note: Return null if last known location is more than 10m old
        });

        if (!location) {
          location = await Location.getCurrentPositionAsync({
            accuracy: Location.Accuracy.High,
            mayShowUserSettingsDialog: false,
          });
        }

        formData.append('recording[latitude]', location.coords.latitude);
        formData.append('recording[longitude]', location.coords.longitude);
      }

      const response = await fetch(`${Api.apiBaseUrl}/recordings.json`, {
        method: 'POST',
        body: formData,
      });

      return await response.json();
    } catch (error) {
      // TODO: Handle errors
      console.log('There was an error during upload.', error);

      setLoading(false);
      loadingAnimation.stop();
      loadingAnimation.reset();

      Animated.sequence([
        Animated.timing(shakeAnim, {toValue: 10, duration: 100, useNativeDriver: Platform.OS !== 'web'}),
        Animated.timing(shakeAnim, {toValue: -10, duration: 100, useNativeDriver: Platform.OS !== 'web'}),
        Animated.timing(shakeAnim, {toValue: 10, duration: 100, useNativeDriver: Platform.OS !== 'web'}),
        Animated.timing(shakeAnim, {toValue: -10, duration: 100, useNativeDriver: Platform.OS !== 'web'}),
        Animated.timing(shakeAnim, {toValue: 10, duration: 100, useNativeDriver: Platform.OS !== 'web'}),
        Animated.timing(shakeAnim, {toValue: 0, duration: 100, useNativeDriver: Platform.OS !== 'web'}),
      ]).start(() => {
        breathingAnimation.start();
      });

      throw error;
    }
  }, [userToken, language, loadingAnimation, shakeAnim, breathingAnimation]);

  const shakeAnim = useRef(new Animated.Value(0)).current;
  const rotationDegree = useRef(new Animated.Value(0)).current;
  const scaleAnim = React.useRef(new Animated.Value(1)).current;

  const breathingAnimation = Animated.loop(
    Animated.sequence([
      Animated.timing(scaleAnim, {toValue: 1.0, duration: 0, useNativeDriver: Platform.OS !== 'web'}),
      Animated.timing(scaleAnim, {toValue: 1.2, duration: 1000, useNativeDriver: Platform.OS !== 'web'}),
      Animated.timing(scaleAnim, {toValue: 1.0, duration: 1500, useNativeDriver: Platform.OS !== 'web'}),
    ])
  );

  useEffect(() => {
    breathingAnimation.start();
  }, []);

  const loadingAnimation = useMemo(() => (
    Animated.loop(Animated.timing(
      rotationDegree,
      {
        toValue: 360,
        duration: 1000,
        easing: Easing.linear,
        useNativeDriver: Platform.OS !== 'web',
      }
    ))
  ), [rotationDegree]);

  const handleLongPress = useCallback(async () => {
    // Note: If this press triggered a permission request we don't want to do anything else
    if (microphonePermissionsRequested) {
      microphonePermissionsRequested = false;
      return;
    }

    speaker.stop();

    if (Platform.OS !== 'web') {
      Haptics.impactAsync(Haptics.ImpactFeedbackStyle.High);
    }

    await recorder.start();
  }, [recorder, speaker]);

  const handlePress = useCallback(async () => {
    // Note: If this press triggered a permission request we don't want to do anything else
    if (microphonePermissionsRequested) {
      microphonePermissionsRequested = false;
      return;
    }

    if (Platform.OS !== 'web') {
      Haptics.notificationAsync(Haptics.NotificationFeedbackType.Error);
    }

    setLastResponse(null);
    setTopText({
      text: t('MainScreen.ChatView.Recorder.errors.holdButton', 'Hold the button while speaking.'),
      style: 'system',
    });

    Animated.sequence([
      Animated.timing(shakeAnim, {toValue: 10, duration: 100, useNativeDriver: Platform.OS !== 'web'}),
      Animated.timing(shakeAnim, {toValue: -10, duration: 100, useNativeDriver: Platform.OS !== 'web'}),
      Animated.timing(shakeAnim, {toValue: 10, duration: 100, useNativeDriver: Platform.OS !== 'web'}),
      Animated.timing(shakeAnim, {toValue: -10, duration: 100, useNativeDriver: Platform.OS !== 'web'}),
      Animated.timing(shakeAnim, {toValue: 10, duration: 100, useNativeDriver: Platform.OS !== 'web'}),
      Animated.timing(shakeAnim, {toValue: 0, duration: 100, useNativeDriver: Platform.OS !== 'web'}),
    ]).start(() => {
      breathingAnimation.start();
    });
  }, []);

  const handlePressIn = useCallback(async () => {
    await recorder.requestPermissions(() => {
      microphonePermissionsRequested = true;
    });

    breathingAnimation.stop();
    breathingAnimation.reset();
  });

  const handlePressOut = useCallback(async () => {
    if (recorder.state.status === 'starting') {
      recorder.stop();
    } else if (recorder.state.status === 'recording') {
      try {
        setLoading(true);
        loadingAnimation.start();

        if (Platform.OS !== 'web') {
          Haptics.impactAsync(Haptics.ImpactFeedbackStyle.High);
        }

        const uri = await recorder.stop();

        const ref = uuid.v4();

        const recording = await handleUploadRecording(uri, ref);

        if (recording.status === 'failed') {
          setLastResponse(null);
          setTopText({
            text: t('MainScreen.ChatView.Recorder.errors.noAudio', 'No audio detected'),
            style: 'system',
          });
        } else {
          setLastResponse(null);
          setTopText({
            text: recording.transcription,
            style: 'transcript',
          });

          const query = await createQuery({
            ref,
            channel: 'recording',
            text: recording.transcription,
            recordingId: recording.id,
          });

          if (Platform.OS !== 'web') {
            Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success);
          }

          if (query) {
            if (query.responses && query.responses.length && query.responses[0].message) {
              setLastResponse(query.responses[0]);
            }
          }
        }

        setLoading(false);
        loadingAnimation.stop();
        loadingAnimation.reset();
      } catch (error) {
        console.log('There was an error during upload.', error);

        if (Platform.OS === 'web') {
          Sentry.Browser.captureException(error);
        } else {
          Sentry.Native.captureException(error);
        }
      }

      breathingAnimation.start();
    }
  }, [breathingAnimation, createQuery, handleUploadRecording, loadingAnimation, recorder, t]);

  return (
    <View style={styles.container}>
      <View
        style={{
          flex: 1,
          alignItems: 'center',
          justifyContent: 'center',
          marginTop: -headerHeight / 2,
          paddingHorizontal: 24,
        }}
      >
        {(topText || {}).text && (
          <View
            style={[
              {
                borderRadius: 20,
                paddingHorizontal: 12,
                paddingVertical: 12,
              },
              styles[`${topText.style || 'system'}TopTextContainer`],
            ]}
          >
            <Text
              style={[
                {
                  color: theme.textPrimary,
                  textAlign: 'center',
                },
                styles[`${topText.style || 'system'}TopTextText`],
              ]}
              numberOfLines={4}
            >
              {topText.text}
            </Text>
          </View>
        )}
      </View>

      <View style={{flex: 1, alignItems: 'center', justifyContent: 'center'}}>
        <TouchableOpacity
          onPress={handlePress}
          onLongPress={handleLongPress}
          onPressIn={handlePressIn}
          onPressOut={handlePressOut}
          style={{alignSelf: 'center', marginTop: -headerHeight}}
        >
          <Animated.View style={{transform: [{translateX: shakeAnim}, {scale: scaleAnim}]}}>
            <View
              style={{
                width: BUTTON_SIZE,
                height: BUTTON_SIZE,
                borderRadius: BUTTON_SIZE / 2,
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: recorder.state.status === 'recording' ? '#fff' : theme.colorPrimary,
              }}
            >
              <CustomProgressBar loading={loading} rotationDegree={rotationDegree}/>

              <Image
                source={require('../../assets/icon-white.png')}
                style={{
                  width: 100,
                  height: 100,
                  tintColor: recorder.state.status === 'recording' ? '#000' : '#fff',
                }}
              />
            </View>
          </Animated.View>
        </TouchableOpacity>
      </View>

      <View
        style={{
          flex: 1,
          alignItems: 'center',
          justifyContent: 'center',
          marginTop: -headerHeight / 2,
        }}
      >
        {lastResponse && (
          <View
            style={{
              position: 'relative',
              maxHeight: 160,
              marginHorizontal: 24,
              borderRadius: 20,
              overflow: 'hidden',
            }}
          >
            <ScrollView
              style={{
                zIndex: -1,
                width: '100%',
              }}
              contentContainerStyle={{
                paddingHorizontal: 12,
                paddingVertical: 12,
                justifyContent: 'center',
                maxWidth: Math.min(Dimensions.get('window').width, 800) - 40,
                backgroundColor: theme.light ? '#ffffff' : '#383838',
                borderRadius: 20,
              }}
              ref={scrollViewRef}
              onScrollBeginDrag={() => {
                shouldScroll = false;
              }}
            >
              <Text style={{color: theme.textPrimary, fontSize: 16}}>
                {lastResponse.message}
              </Text>

              {(lastResponse.cards || []).length > 0 && lastResponse.cards.find(({card_type}) => card_type === 'place') && (
                <PlacesMap cards={lastResponse.cards}/>
              )}

              {(lastResponse.cards || []).length > 0 && (
                <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{flexGrow: 0}}>
                  {lastResponse.cards.map((card, i) => (
                    <Card
                      key={card.id}
                      card={card}
                      isLast={i === lastResponse.cards.length - 1}
                    />
                  ))}
                </ScrollView>
              )}
            </ScrollView>
          </View>
        )}
      </View>
    </View>
  );
};


const getStyles = theme => StyleSheet.create({
  container: {
    height: '100%',
    width: '100%',
    maxWidth: Math.min(Dimensions.get('window').width, 800),
    justifyContent: 'center',
  },
  systemTopTextContainer: {},
  systemTopTextText: {
    fontSize: 20,
  },
  transcriptTopTextContainer: {
    backgroundColor: theme.light ? '#ffffff' : '#383838',
  },
  transcriptTopTextText: {
    fontSize: 16,
    fontWeight: 'bold',
  },
});

export default MicrophoneView;
